import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useSession } from '../providers/sessionContext'
import { safeRedirectOrDefault } from '../../lib/redirect'
import {
  AccessResolutionError,
  AuthLoading,
  NoMembership,
  OrganisationSuspended,
  PermissionDeniedPage,
} from '../../features/auth/AccessStatePages'

/**
 * Route guards.
 *
 * These decide what renders. They do not decide what a user may read: Row Level
 * Security does that, independently, and would still refuse if every guard here
 * were deleted. What guards buy is a comprehensible product. Without them a
 * viewer who types an administration URL gets a screen full of empty tables and
 * failed requests instead of being told plainly that it is not for them.
 *
 * The load-bearing rule is that **no guard decides anything while the session is
 * still resolving**. A guard that treats "not known yet" as "signed out"
 * bounces a signed-in user to the login page on every refresh, and one that
 * treats it as "allowed" flashes protected content. Both show up only under
 * slow networks, which is where nobody tests.
 */

/**
 * Where to send a signed-out visitor, carrying where they were going.
 *
 * Every guard uses this. Three of them used to navigate to a bare `/login`,
 * which threw the destination away and dropped the user on the default landing
 * route after they signed in. For a platform administrator that default is a
 * tenant route, and a platform administrator deliberately has no tenant, so the
 * journey ended on "No organisation yet" with no way back to the screen they
 * had asked for.
 *
 * The path is read back through the redirect allowlist in `lib/redirect` rather
 * than trusted, because it travels through the URL.
 */
function signInAndReturn(location: ReturnType<typeof useLocation>) {
  const attempted = `${location.pathname}${location.search}`
  return <Navigate to={`/login?next=${encodeURIComponent(attempted)}`} replace />
}

/**
 * Requires a signed-in user with a usable membership.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <AuthLoading />
  if (session.status === 'signed-out') return signInAndReturn(location)

  const blocked = blockingStateFor(session)
  if (blocked !== null) return blocked

  return <>{children}</>
}

/**
 * The state page a signed-in user should see when they cannot proceed.
 *
 * Takes the session as an argument and calls no hooks, so it is safe to call
 * after an early return. Naming it use* would say the opposite.
 */
function blockingStateFor(session: ReturnType<typeof useSession>): ReactNode | null {
  if (session.status === 'access-error') return <AccessResolutionError />
  if (!session.hasMembership) return <NoMembership />
  return null
}

/**
 * A route that must not be used while the organisation is suspended.
 *
 * Suspension makes a tenant read-only for everyone including its owner, so this
 * wraps anything that writes. Read-only routes deliberately stay reachable, so
 * a suspended organisation can still be reviewed and exported.
 */
export function RequireActiveOrganisation({ children }: { children: ReactNode }) {
  const session = useSession()
  if (session.status === 'loading') return <AuthLoading />
  if (session.organisationSuspended) return <OrganisationSuspended />
  return <>{children}</>
}

export function RequireRole({
  allow,
  children,
}: {
  allow: readonly ('owner' | 'admin' | 'viewer')[]
  children: ReactNode
}) {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <AuthLoading />
  if (session.status === 'signed-out') return signInAndReturn(location)
  if (session.status === 'access-error') return <AccessResolutionError />
  if (!session.hasMembership) return <NoMembership />
  if (session.role === null || !allow.includes(session.role)) return <PermissionDeniedPage />

  return <>{children}</>
}

/**
 * Financial data: wage rates, salaries, labour cost, margin, leakage.
 *
 * Mirrors `app_private.can_view_financials`. Owners and admins hold it
 * implicitly; the flag only ever elevates a viewer (ADR-003). If this guard and
 * the policy ever disagree, the policy wins and the user sees an empty screen,
 * which is the right way round.
 */
export function RequireFinancialAccess({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <AuthLoading />
  if (session.status === 'signed-out') return signInAndReturn(location)
  if (session.status === 'access-error') return <AccessResolutionError />
  if (!session.hasMembership) return <NoMembership />
  if (!session.canViewFinancials) return <PermissionDeniedPage />

  return <>{children}</>
}

/**
 * Platform administration. Not a tenant role: it is held in platform_admins and
 * granted out of band, so it is checked independently of membership.
 *
 * Membership is deliberately not consulted, and must not be. A platform
 * administrator belongs to no client organisation by design, so requiring one
 * here would either lock them out or invite somebody to invent a placeholder
 * tenant to satisfy a guard.
 */
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <AuthLoading />
  if (session.status === 'signed-out') return signInAndReturn(location)
  if (session.status === 'access-error') return <AccessResolutionError />
  if (!session.isPlatformAdmin) return <PermissionDeniedPage />

  return <>{children}</>
}

/**
 * Keeps a signed-in user off the login page.
 *
 * It honours `next`, and it has to. The login page also navigates to `next`
 * after a successful sign-in, but the session resolves as part of that same
 * sign-in, so this guard re-renders and navigates first. Hard-coding `/` here
 * meant this always won and the login page's own destination was never used:
 * every sign-in landed on the tenant default no matter what the user had asked
 * for. For a platform administrator, who has no tenant, that default is the
 * "No organisation yet" page.
 *
 * Read back through the same allowlist as everywhere else, because it arrives
 * in the URL.
 */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()

  if (session.status === 'loading') return <AuthLoading />
  if (session.status === 'signed-out') return <>{children}</>

  const next = new URLSearchParams(location.search).get('next')
  return <Navigate to={safeRedirectOrDefault(next)} replace />
}
